import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import DashLayout from "../components/DashLayout";
import api, { formatApiErrorDetail } from "../lib/api";
import { Lock, LockOpen, Trash2, ChevronLeft, Copy, Globe, Pause, Play } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";
import { useAuth } from "../context/AuthContext";

function MethodBadge({ m }) {
    return <span className={`method-${m} font-mono font-bold text-xs px-2 py-0.5 border border-current`}>{m}</span>;
}
function StatusBadge({ s }) {
    const cls = s < 300 ? "status-2xx" : s < 400 ? "status-3xx" : s < 500 ? "status-4xx" : "status-5xx";
    return <span className={`${cls} font-mono font-bold text-sm`}>{s}</span>;
}

export default function TunnelDetail() {
    const { user } = useAuth();
    const { tid } = useParams();
    const [tunnel, setTunnel] = useState(null);
    const [logs, setLogs] = useState([]);
    const [selected, setSelected] = useState(null);
    const [paused, setPaused] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [activeTab, setActiveTab] = useState("headers");
    const [replaying, setReplaying] = useState(false);

    // Settings form
    const [pwToggle, setPwToggle] = useState(false);
    const [pw, setPw] = useState("");
    const [customDomain, setCustomDomain] = useState("");

    const pollRef = useRef(null);

    const loadTunnel = useCallback(async () => {
        try {
            const { data } = await api.get(`/tunnels/${tid}`);
            setTunnel(data);
            setPwToggle(data.password_protect);
            setCustomDomain(data.custom_domain || "");
        } catch {
            toast.error("Tunnel not found");
        }
    }, [tid]);

    const loadLogs = useCallback(async () => {
        if (paused) return;
        try {
            const { data } = await api.get(`/requests/tunnel/${tid}?limit=80`);
            setLogs(data);
        } catch {}
    }, [tid, paused]);

    useEffect(() => { loadTunnel(); }, [loadTunnel]);
    useEffect(() => {
        loadLogs();
        pollRef.current = setInterval(loadLogs, 2000);
        return () => clearInterval(pollRef.current);
    }, [loadLogs]);

    const togglePassword = async () => {
        try {
            const next = !pwToggle;
            const body = { password_protect: next };
            if (next && pw) body.password = pw;
            await api.patch(`/tunnels/${tid}`, body);
            toast.success(next ? "Password protect enabled" : "Password protect disabled");
            setPwToggle(next);
            loadTunnel();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
        }
    };

    const saveDomain = async () => {
        try {
            await api.patch(`/tunnels/${tid}`, { custom_domain: customDomain });
            toast.success("Custom domain saved — verify CNAME to activate");
            loadTunnel();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
        }
    };

    const verifyDomain = async () => {
        try {
            await api.post(`/tunnels/${tid}/verify-domain`);
            toast.success("CNAME verified — domain is live");
            loadTunnel();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
        }
    };

    const clearLogs = async () => {
        if (!confirm("Clear all logs for this tunnel?")) return;
        try {
            await api.delete(`/requests/tunnel/${tid}`);
            setLogs([]);
            setSelected(null);
            toast.success("Logs cleared");
        } catch {
            toast.error("Failed");
        }
    };

    const replayRequest = async (rid) => {
        if (replaying) return;
        setReplaying(true);
        try {
            await api.post(`/requests/${rid}/replay`);
            toast.success("Request replayed successfully");
            loadLogs();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to replay request");
        } finally {
            setReplaying(false);
        }
    };

    const copyUrl = () => {
        if (!tunnel) return;
        navigator.clipboard.writeText(tunnel.public_url);
        toast.success("URL copied");
    };

    if (!tunnel) {
        return (
            <DashLayout title="LOADING..." subtitle="tunnel">
                <div className="text-[#52525B] text-sm font-mono">[ loading tunnel data... ]</div>
            </DashLayout>
        );
    }

    return (
        <DashLayout
            title={tunnel.subdomain}
            subtitle={`tunnels / ${tunnel.subdomain}`}
            action={
                <div className="flex items-center gap-2">
                    <Link to="/app/tunnels" className="tf-btn tf-btn-ghost" data-testid="back-to-tunnels">
                        <ChevronLeft className="w-4 h-4" /> BACK
                    </Link>
                    <button onClick={() => setShowSettings(!showSettings)} className="tf-btn" data-testid="toggle-tunnel-settings">
                        SETTINGS
                    </button>
                </div>
            }
        >
            {/* Top bar */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-0 border border-[#27272A] mb-6" data-testid="tunnel-summary">
                <div className="p-5 border-r border-b lg:border-b-0 border-[#27272A]">
                    <div className="label-mono">PUBLIC URL</div>
                    <div className="flex items-center gap-2 mt-1">
                        <a href={tunnel.public_url} target="_blank" rel="noreferrer" className="text-[#00FF41] font-mono text-sm truncate hover:underline" data-testid="tunnel-public-url">{tunnel.public_url}</a>
                        <button onClick={copyUrl} className="text-[#52525B] hover:text-[#00FF41]" data-testid="copy-url-btn">
                            <Copy className="w-4 h-4" />
                        </button>
                    </div>
                </div>
                <div className="p-5 border-r border-b lg:border-b-0 border-[#27272A]">
                    <div className="label-mono">STATUS</div>
                    <div className="font-display text-2xl font-bold mt-1 flex items-center gap-2">
                        <span className={`w-2 h-2 ${tunnel.status === "online" ? "bg-[#00FF41]" : "bg-[#52525B]"}`}></span>
                        <span className={tunnel.status === "online" ? "text-[#00FF41]" : "text-[#52525B]"}>{tunnel.status.toUpperCase()}</span>
                    </div>
                </div>
                <div className="p-5 border-r border-b lg:border-b-0 border-[#27272A]">
                    <div className="label-mono">REQUESTS</div>
                    <div className="font-display text-2xl font-bold mt-1">{tunnel.request_count}</div>
                </div>
                <div className="p-5">
                    <div className="label-mono">FORWARDING</div>
                    <div className="font-mono text-[#FAFAFA] text-sm mt-1">localhost:{tunnel.local_port}</div>
                </div>
            </div>

            {/* Traffic Chart */}
            <div className="border border-[#27272A] mb-6 p-5 bg-[#0A0A0A]">
                <div className="label-mono text-[#00FF41] mb-4">▸ REQUEST TRAFFIC (LAST 10 MINUTES)</div>
                <div className="h-44 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={prepareChartData(logs)}>
                            <defs>
                                <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#00FF41" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#00FF41" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="time" stroke="#52525B" fontSize={10} fontStyle="monospace" />
                            <YAxis stroke="#52525B" fontSize={10} fontStyle="monospace" allowDecimals={false} />
                            <Tooltip contentStyle={{ background: "#050505", border: "1px solid #27272A", fontFamily: "monospace", fontSize: "12px", color: "#FAFAFA" }} />
                            <Area type="monotone" dataKey="requests" stroke="#00FF41" fillOpacity={1} fill="url(#colorRequests)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {showSettings && (
                <div className="grid lg:grid-cols-2 gap-0 border border-[#27272A] mb-6" data-testid="tunnel-settings-panel">
                    <div className="p-6 border-r border-[#27272A]">
                        <div className="label-mono text-[#00FF41] mb-2">// AUTH</div>
                        <h4 className="font-display text-xl font-bold uppercase mb-3">Password Protect</h4>
                        <p className="text-sm text-[#A1A1AA] mb-4">Toggle a basic auth gate on the public URL. Takes effect instantly — no tunnel restart needed.</p>
                        {pwToggle && (
                            <input className="tf-input pl-2 mb-4" placeholder="Set new password (optional)" type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="tunnel-password-input" />
                        )}
                        <button onClick={togglePassword} className={pwToggle ? "tf-btn tf-btn-danger" : "tf-btn"} data-testid="toggle-password-btn">
                            {pwToggle ? <><LockOpen className="w-4 h-4" /> DISABLE</> : <><Lock className="w-4 h-4" /> ENABLE PROTECT</>}
                        </button>
                    </div>
                    <div className="p-6">
                        <div className="label-mono text-[#FF9900] mb-2">// PRO · CUSTOM DOMAIN</div>
                        <h4 className="font-display text-xl font-bold uppercase mb-3">Bring Your Domain</h4>
                        <p className="text-sm text-[#A1A1AA] mb-4">CNAME → <span className="text-[#00FF41] font-mono">cname.{user?.tunnel_domain || "synapselink.cloud"}</span></p>
                        <input className="tf-input pl-2 mb-3" placeholder="api.yourdomain.com" value={customDomain} onChange={(e) => setCustomDomain(e.target.value)} data-testid="tunnel-domain-input" />
                        <div className="flex gap-2 mb-2">
                            <button onClick={saveDomain} className="tf-btn tf-btn-ghost" data-testid="save-domain-btn"><Globe className="w-4 h-4" /> SAVE</button>
                            {tunnel.custom_domain && tunnel.custom_domain_status !== "active" && (
                                <button onClick={verifyDomain} className="tf-btn" data-testid="verify-domain-btn">VERIFY DNS</button>
                            )}
                        </div>
                        {tunnel.custom_domain_status && (
                            <div className="text-xs uppercase tracking-wider mt-2">
                                Status: <span className={tunnel.custom_domain_status === "active" ? "text-[#00FF41]" : "text-[#FF9900]"}>{tunnel.custom_domain_status.replace("_", " ")}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Inspector */}
            <div className="grid lg:grid-cols-12 gap-0 border border-[#27272A] min-h-[560px]" data-testid="request-inspector">
                {/* Feed */}
                <div className="lg:col-span-5 border-r border-[#27272A] flex flex-col">
                    <div className="px-4 py-3 border-b border-[#27272A] bg-[#0A0A0A] flex items-center justify-between">
                        <div className="label-mono text-[#00FF41]">▸ LIVE FEED · {logs.length}</div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => setPaused(!paused)} className="text-[#A1A1AA] hover:text-[#FAFAFA] text-xs uppercase tracking-wider flex items-center gap-1" data-testid="pause-feed-btn">
                                {paused ? <><Play className="w-3 h-3"/>RESUME</> : <><Pause className="w-3 h-3"/>PAUSE</>}
                            </button>
                            <button onClick={clearLogs} className="text-[#52525B] hover:text-[#FF3B30]" data-testid="clear-logs-btn">
                                <Trash2 className="w-3.5 h-3.5"/>
                            </button>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto max-h-[640px]">
                        {logs.length === 0 ? (
                            <div className="p-8 text-center text-[#52525B] text-sm font-mono">
                                [ waiting for traffic... ]
                                <div className="mt-2 text-xs">Run <span className="text-[#00FF41]">synapselink {tunnel.local_port}</span> to start tunneling.</div>
                            </div>
                        ) : (
                            logs.map((l) => (
                                <button
                                    key={l.id}
                                    onClick={() => setSelected(l)}
                                    data-testid={`log-row-${l.id}`}
                                    className={`w-full text-left px-4 py-2.5 border-b border-[#18181B] flex items-center gap-3 hover:bg-[#0A0A0A] ${selected?.id === l.id ? "bg-[#0F0F0F] border-l-2 border-l-[#00FF41]" : ""}`}
                                >
                                    <span className="text-[10px] text-[#52525B] font-mono w-16 shrink-0">{new Date(l.timestamp).toLocaleTimeString("en-GB")}</span>
                                    <MethodBadge m={l.method} />
                                    <span className="font-mono text-xs text-[#FAFAFA] truncate flex-1">{l.path}</span>
                                    <StatusBadge s={l.status} />
                                    <span className="text-[10px] text-[#52525B] font-mono w-12 text-right shrink-0">{l.duration_ms}ms</span>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* Detail */}
                <div className="lg:col-span-7 flex flex-col">
                    {selected ? (
                        <>
                            <div className="px-5 py-3 border-b border-[#27272A] bg-[#0A0A0A] flex items-center gap-3 flex-wrap">
                                <MethodBadge m={selected.method} />
                                <span className="font-mono text-[#FAFAFA] text-sm break-all">{selected.path}</span>
                                <div className="ml-auto flex items-center gap-3">
                                    <StatusBadge s={selected.status} />
                                    <span className="text-xs text-[#52525B] font-mono hidden sm:inline">{selected.duration_ms}ms · {selected.size_bytes}b</span>
                                    <button 
                                        disabled={replaying} 
                                        onClick={() => replayRequest(selected.id)} 
                                        className="tf-btn tf-btn-ghost text-xs py-1 px-2.5 border border-[#00FF41] text-[#00FF41] hover:bg-[#00FF41] hover:text-[#050505] disabled:opacity-50"
                                        data-testid="replay-request-btn"
                                    >
                                        {replaying ? "REPLAYING..." : "REPLAY"}
                                    </button>
                                </div>
                            </div>
                            
                            {/* Tab navigation */}
                            <div className="border-b border-[#27272A] bg-[#0A0A0A] flex font-mono text-xs">
                                <button 
                                    onClick={() => setActiveTab("headers")} 
                                    className={`px-4 py-2.5 border-r border-[#27272A] ${activeTab === "headers" ? "bg-[#0F0F0F] text-[#00FF41] font-bold" : "text-[#52525B] hover:text-[#FAFAFA]"}`}
                                >
                                    HEADERS
                                </button>
                                <button 
                                    onClick={() => setActiveTab("payload")} 
                                    className={`px-4 py-2.5 border-r border-[#27272A] ${activeTab === "payload" ? "bg-[#0F0F0F] text-[#00FF41] font-bold" : "text-[#52525B] hover:text-[#FAFAFA]"}`}
                                >
                                    PAYLOAD
                                </button>
                                <button 
                                    onClick={() => setActiveTab("response")} 
                                    className={`px-4 py-2.5 border-r border-[#27272A] ${activeTab === "response" ? "bg-[#0F0F0F] text-[#00FF41] font-bold" : "text-[#52525B] hover:text-[#FAFAFA]"}`}
                                >
                                    RESPONSE
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-5 space-y-6 max-h-[580px] font-mono text-sm">
                                {activeTab === "headers" && (
                                    <div className="space-y-6">
                                        <div>
                                            <div className="label-mono text-[#00FF41] mb-2">▸ REQUEST HEADERS</div>
                                            <HeaderList headers={selected.headers} />
                                        </div>
                                        <div>
                                            <div className="label-mono text-[#00FF41] mb-2">▸ RESPONSE HEADERS</div>
                                            <HeaderList headers={selected.response_headers} />
                                        </div>
                                    </div>
                                )}

                                {activeTab === "payload" && (
                                    <div>
                                        <div className="label-mono text-[#00FF41] mb-2">▸ DECODED BODY</div>
                                        <PrettyPrint content={tryDecodeBase64(selected.body)} />
                                    </div>
                                )}

                                {activeTab === "response" && (
                                    <div>
                                        <div className="label-mono text-[#00FF41] mb-2">▸ DECODED RESPONSE BODY</div>
                                        <PrettyPrint content={tryDecodeBase64(selected.response_body)} />
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-x-6 gap-y-2 pt-4 border-t border-[#27272A]">
                                    <Field k="Remote IP" v={selected.remote_ip} />
                                    <Field k="Timestamp" v={new Date(selected.timestamp).toLocaleString()} />
                                    <Field k="Duration" v={`${selected.duration_ms} ms`} />
                                    <Field k="Size" v={`${selected.size_bytes} bytes`} />
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center p-10 text-center">
                            <div>
                                <div className="font-display text-5xl font-bold text-[#27272A] mb-4">// SELECT</div>
                                <p className="text-[#52525B] font-mono text-sm">Click a request from the live feed to inspect headers, body, and response.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </DashLayout>
    );
}

function tryDecodeBase64(str) {
    if (!str) return "";
    try {
        const decoded = atob(str);
        try {
            return JSON.parse(decoded);
        } catch {
            return decoded;
        }
    } catch {
        return str;
    }
}

function PrettyPrint({ content }) {
    if (!content) return <div className="text-xs text-[#52525B] font-mono italic">[ empty ]</div>;
    const isObject = typeof content === "object";
    const displayStr = isObject ? JSON.stringify(content, null, 2) : content;
    return (
        <pre className="bg-[#050505] border border-[#18181B] p-3 overflow-x-auto text-xs leading-relaxed text-[#A1A1AA] whitespace-pre-wrap break-all font-mono">
            {displayStr}
        </pre>
    );
}

function HeaderList({ headers }) {
    if (!headers || Object.keys(headers).length === 0) {
        return <div className="text-xs text-[#52525B] font-mono italic">[ none ]</div>;
    }
    return (
        <div className="bg-[#050505] border border-[#18181B] divide-y divide-[#18181B] font-mono text-xs text-[#A1A1AA]">
            {Object.entries(headers).map(([key, val]) => (
                <div key={key} className="flex p-2 gap-2">
                    <span className="text-[#FAFAFA] font-bold select-all shrink-0 w-1/3 border-r border-[#18181B] pr-2 break-all">{key}</span>
                    <span className="select-all break-all pl-2 flex-1">{String(val)}</span>
                </div>
            ))}
        </div>
    );
}

function Field({ k, v }) {
    return (
        <div className="flex flex-col">
            <span className="label-mono">{k}</span>
            <span className="text-[#FAFAFA] text-xs">{v}</span>
        </div>
    );
}

function prepareChartData(logs) {
    if (!logs) return [];
    const groups = {};
    const now = new Date();
    // Seed last 10 minutes
    for (let i = 9; i >= 0; i--) {
        const d = new Date(now.getTime() - i * 60 * 1000);
        const timeStr = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
        groups[timeStr] = 0;
    }
    
    // Group logs
    logs.forEach(log => {
        try {
            const logTime = new Date(log.timestamp);
            const timeStr = logTime.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
            if (groups[timeStr] !== undefined) {
                groups[timeStr] += 1;
            }
        } catch {}
    });
    
    return Object.entries(groups)
        .map(([time, count]) => ({ time, requests: count }))
        .sort((a, b) => a.time.localeCompare(b.time));
}
